import React from 'react';
import ToolCard from '../components/common/ToolCard';
import SEO from "../components/common/SEO";

const tools = [
    {
        title: "Case Converter",
        description:
            "Convert between camelCase, snake_case, kebab-case, PascalCase, and more.",
        path: "/case-converter",
        icon: "text-format",
    },
    {
        title: "Git Helper",
        description:
            "Generate common Git commands based on branch names and operations.",
        path: "/git-helper",
        icon: "git-branch",
    },
    {
        title: "JIRA Ticket Creator",
        description:
            "Create JIRA tickets with predefined templates and AI assistance.",
        path: "/jira-creator",
        icon: "ticket",
    },
    {
        title: "Diff Tool",
        description:
            "Compare two texts and highlight differences between them.",
        path: "/diff-tool",
        icon: "diff",
    },
    {
        title: "List Tool",
        description:
            "Remove duplicate lines from text or code with various options.",
        path: "/duplicate-remover",
        icon: "filter",
    },
    {
        title: "JSON ↔ YAML Converter",
        description: "Convert between JSON and YAML formats with ease.",
        path: "/json-yaml-converter",
        icon: "code",
    },
    {
        title: "UUID Generator",
        description:
            "Generate random UUIDs in different formats (v1, v3, v4, v5, v7).",
        path: "/uuid-generator",
        icon: "key",
    },
    {
        title: "Image Resizer",
        description:
            "Resize images quickly with a slider and download in different formats.",
        path: "/image-resizer",
        icon: "photo",
    },
    {
        title: "React Generator",
        description:
            "Generate React components and code snippets with customizable options.",
        path: "/react-generator",
        icon: "brand-react",
    },
];

const Home: React.FC = () => {
  const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Dev's Best Friend",
      description: "A collection of free developer tools to boost productivity",
      applicationCategory: "DeveloperApplication",
      offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
      },
      operatingSystem: "Any",
      softwareHelp: {
          "@type": "CreativeWork",
          url: "https://devsbestfriend.com/about",
      },
      author: {
          "@type": "Organization",
          name: "Dev's Best Friend",
          url: "https://devsbestfriend.com",
      },
      sameAs: ["https://github.com/devsbestfriend"],
  };

  return (
      <div>
          <SEO
              title="Developer Tools Collection | Case Converter, Git Helper & More"
              description="Free developer tools collection: Case Converter, Git Helper, JIRA Creator, JSON/YAML Converter, Diff Tool, List Tool, UUID Generator and more to boost your productivity."
              canonicalPath="/"
              structuredData={structuredData}
          />

          <section className="mb-12">
              <div className="max-w-3xl">
                  <h1 className="text-4xl font-bold text-primary dark:text-white">
                      Dev's Best Friend - Developer Tools Collection
                  </h1>
                  <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
                      A comprehensive collection of free browser-based developer
                      tools designed to make your development workflow smoother,
                      faster, and more efficient.
                  </p>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                      All tools work directly in your browser - no installation
                      required. Perfect for developers, programmers, and
                      software engineers looking to boost productivity.
                  </p>
              </div>
          </section>

          <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-primary dark:text-white">
                  Available Developer Tools
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tools.map((tool) => (
                      <ToolCard
                          key={tool.path}
                          title={tool.title}
                          description={tool.description}
                          path={tool.path}
                          icon={tool.icon}
                      />
                  ))}
              </div>
          </section>

          <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4 text-primary dark:text-white">
                  Why Use Dev's Best Friend?
              </h2>
              <div className="space-y-4">
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-primary dark:text-white">
                          Free & Browser-Based
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300">
                          All tools are completely free to use and run directly
                          in your browser - no downloads, installations, or
                          sign-ups required.
                      </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-primary dark:text-white">
                          Time-Saving
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300">
                          Our developer-focused tools help automate repetitive
                          tasks and streamline common operations in your
                          workflow.
                      </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                      <h3 className="text-lg font-semibold text-primary dark:text-white">
                          Privacy-Focused
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300">
                          We process everything locally in your browser - your
                          data never leaves your device.
                      </p>
                  </div>
              </div>
          </section>

          <section className="mt-16 max-w-3xl">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-blue-800 dark:text-blue-300">
                      Keyboard Shortcuts
                  </h2>
                  <p className="mt-2 text-blue-700 dark:text-blue-400">
                      DevsBestFriend supports keyboard shortcuts to speed up
                      your workflow. Press{" "}
                      <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-sm">
                          ?
                      </kbd>{" "}
                      anywhere to see available shortcuts.
                  </p>
              </div>
          </section>
      </div>
  );
};

export default Home;
